import { makeStyles, tokens } from '@fluentui/react-components'
import {
  CONTEXT_ENTRY_ICON_GAP,
  CONTEXT_ENTRY_TEXT_INSET,
  contextEntrySection,
  contextEntryText,
} from './context-entry-styles'

export const useStyles = makeStyles({
  card: {
    position: 'relative',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow4,
    padding: tokens.spacingHorizontalXS,
    margin: `0 ${tokens.spacingHorizontalXS}`,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXS,
    boxSizing: 'border-box',
    flexShrink: 0,
    overflow: 'hidden',
    transitionProperty: 'background-color, box-shadow',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
    cursor: 'pointer',
    ['&:hover']: {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      boxShadow: tokens.shadow8,
    },
    ['&:active']: {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
      boxShadow: tokens.shadow2,
    },
    ['& > *']: {
      position: 'relative',
      zIndex: 1,
    },
    ['&::before']: {
      content: '""',
      position: 'absolute',
      inset: 0,
      zIndex: 0,
      pointerEvents: 'none',
      opacity: 0,
      transform: 'translateY(-8%)',
      backgroundImage: `linear-gradient(180deg, ${tokens.colorNeutralBackground2} 0%, ${tokens.colorNeutralBackground3} 100%)`,
      transitionProperty: 'opacity, transform',
      transitionDuration: tokens.durationNormal,
      transitionTimingFunction: tokens.curveEasyEase,
    },
    ['& .context-dismiss-btn']: {
      opacity: 0,
      transitionProperty: 'opacity',
      transitionDuration: tokens.durationFaster,
      transitionTimingFunction: tokens.curveEasyEase,
    },
    ['&:hover .context-dismiss-btn']: {
      opacity: 1,
    },
    ['&:focus-within .context-dismiss-btn']: {
      opacity: 1,
    },
  },
  cardWholeFileSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    ['&:hover']: {
      backgroundColor: tokens.colorBrandBackground2Hover,
      boxShadow: tokens.shadow8,
    },
    ['&:active']: {
      backgroundColor: tokens.colorBrandBackground2Pressed,
      boxShadow: tokens.shadow2,
    },
    ['&:focus-within']: {
      backgroundColor: tokens.colorBrandBackground2Hover,
      boxShadow: tokens.shadow8,
    },
  },
  cardSectionInteraction: {
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    ['&:hover']: {
      backgroundColor: tokens.colorNeutralBackground1,
      boxShadow: tokens.shadow4,
    },
    ['&:active']: {
      backgroundColor: tokens.colorNeutralBackground1,
      boxShadow: tokens.shadow4,
    },
  },
  cardPaused: {
    backgroundColor: tokens.colorNeutralBackground2,
    ['&::before']: {
      opacity: 1,
      transform: 'translateY(0)',
    },
    ['& .fui-Caption1']: {
      color: tokens.colorNeutralForeground2,
    },
    ['& .context-section-icon']: {
      color: tokens.colorNeutralForegroundDisabled,
    },
    ['& .fui-Switch__label']: {
      color: tokens.colorNeutralForegroundDisabled,
    },
    ['& .fui-Button']: {
      color: tokens.colorNeutralForegroundDisabled,
    },
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minWidth: 0,
    padding: tokens.spacingHorizontalXXS,
  },
  titleLeft: {
    display: 'flex',
    gap: CONTEXT_ENTRY_ICON_GAP,
    alignItems: 'center',
    overflow: 'hidden',
    minWidth: 0,
    flex: '1 1 0',
  },
  titleText: {
    color: tokens.colorNeutralForeground1,
    ...contextEntryText,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
    paddingLeft: CONTEXT_ENTRY_TEXT_INSET,
  },
  titleLinkButton: {
    justifyContent: 'flex-start',
    padding: 0,
    minWidth: 0,
    maxWidth: '100%',
    color: tokens.colorNeutralForeground1,
    textDecorationLine: 'none',
    ['&:hover, &:focus-visible']: {
      color: tokens.colorNeutralForeground1,
      textDecorationLine: 'underline',
    },
  },
  buttonTextContainer: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  titleTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flex: '1 1 0',
    overflow: 'hidden',
  },
  titleSubtitle: {
    color: tokens.colorNeutralForeground2,
    paddingLeft: CONTEXT_ENTRY_TEXT_INSET,
  },
  sectionsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalXS,
  },
  sectionsGridSingle: {
    gridTemplateColumns: '1fr',
  },
  sectionTag: {
    display: 'flex',
    alignItems: 'center',
    ...contextEntrySection,
    backgroundColor: tokens.colorBrandBackground2,
    borderRadius: tokens.borderRadiusCircular,
    overflow: 'hidden',
    minWidth: 0,
    transitionProperty: 'background-color',
    transitionDuration: tokens.durationFaster,
    transitionTimingFunction: tokens.curveEasyEase,
    ['&:hover']: {
      backgroundColor: tokens.colorBrandBackground2Hover,
      boxShadow: tokens.shadow2,
    },
    ['&:active']: {
      backgroundColor: tokens.colorBrandBackground2Pressed,
      boxShadow: tokens.shadow2,
    },
    ['&:focus-within']: {
      backgroundColor: tokens.colorBrandBackground2Hover,
      boxShadow: tokens.shadow2,
    },
    ['& .section-dismiss-btn']: {
      opacity: 0,
      transitionProperty: 'opacity',
      transitionDuration: tokens.durationFaster,
      transitionTimingFunction: tokens.curveEasyEase,
    },
    ['&:hover .section-dismiss-btn']: {
      opacity: 1,
    },
    ['&:focus-within .section-dismiss-btn']: {
      opacity: 1,
    },
  },
  sectionIcon: {
    width: '16px',
    height: '16px',
    flexShrink: 0,
    color: tokens.colorNeutralForeground1,
  },
  sectionTagText: {
    color: tokens.colorNeutralForeground1,
    ...contextEntryText,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
    flex: 1,
  },
  sectionLinkButton: {
    justifyContent: 'flex-start',
    padding: 0,
    minWidth: 0,
    maxWidth: '100%',
    color: tokens.colorNeutralForeground1,
    textDecorationLine: 'none',
    ['&:hover, &:focus-visible']: {
      color: tokens.colorNeutralForeground1,
      textDecorationLine: 'underline',
    },
  },
  dismissButton: {
    color: tokens.colorNeutralForeground2,
  },
  sectionDismissButton: {
    color: tokens.colorNeutralForeground1,
    flexShrink: 0,
    backgroundColor: 'transparent',
    borderRadius: tokens.borderRadiusCircular,
    width: tokens.spacingHorizontalL,
    minWidth: tokens.spacingHorizontalL,
    height: tokens.spacingVerticalL,
    minHeight: tokens.spacingVerticalL,
    padding: tokens.spacingHorizontalNone,
    ['&:hover']: {
      backgroundColor: tokens.colorBrandBackground2Hover,
      color: tokens.colorNeutralForeground1,
    },
    ['&:active']: {
      backgroundColor: tokens.colorBrandBackground2Pressed,
      color: tokens.colorNeutralForeground1,
    },
    ['&:focus-visible']: {
      backgroundColor: tokens.colorBrandBackground2Hover,
      color: tokens.colorNeutralForeground1,
    },
    ['& .fui-Button__icon']: {
      fontSize: tokens.fontSizeBase200,
      lineHeight: tokens.lineHeightBase200,
    },
  },
  switch: {
    color: tokens.colorNeutralStrokeAccessible,
    display: 'flex',
    alignItems: 'center',
    padding: tokens.spacingHorizontalNone,
    margin: tokens.spacingHorizontalNone,
    minHeight: '20px',
    height: '20px',
    ['& .fui-Switch__indicator']: {
      margin: tokens.spacingHorizontalNone,
    },
  },
})
